import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { PiggyBank } from "lucide-react";
import ProductCard from "../components/ProductCard";
import PromoBannerRotator from "../components/PromoBannerRotator";
import { CATEGORIES, productsByCategory } from "../data/products";
import { useCatalog } from "../context/CatalogContext";
import { useCart } from "../context/CartContext";
import SEO from "../components/SEO";

export default function Products() {
  const { products } = useCatalog();
  const { activeSeasonal } = useCart();
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const sectionRefs = useRef({});
  const location = useLocation();

  // If URL has a hash like #endocrine, scroll to that section on load.
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash && sectionRefs.current[hash]) {
      setTimeout(() => {
        sectionRefs.current[hash].scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [location.hash]);

  // Update active chip on scroll (intersection-based)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the first section whose top is just below the sticky chip bar.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.getAttribute("data-category-id");
          if (id) setActiveCategory(id);
        }
      },
      { rootMargin: "-160px 0px -55% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChipClick = (id) => {
    setActiveCategory(id);
    const target = sectionRefs.current[id];
    if (target) {
      const y = target.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <main data-testid="products-page" className="pb-20 md:pb-28">
      <SEO
        title="Research Compounds Catalog"
        description="Browse the full LuviaRx catalog of premium research peptides and compounds. Third-party tested, batch-traceable, and shipped from Boca Raton, FL. For Research Use Only."
        path="/products"
      />
      {/* Rotating promo banner — Researcher Welcome Offer ⇄ Grand Opening. */}
      <PromoBannerRotator />

      {/* Volume discount banner */}
      <section className="container-x pt-4 md:pt-6">
        <div
          data-testid="volume-discount-banner"
          className="rounded-2xl px-5 sm:px-7 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
          style={{ background: "rgba(139, 34, 82, 0.06)" }}
        >
          <div className="flex items-center gap-3 shrink-0">
            <span
              className="w-9 h-9 rounded-full bg-white/85 flex items-center justify-center"
              style={{ color: "#8B2252" }}
            >
              <PiggyBank className="w-4 h-4" strokeWidth={2} />
            </span>
            <span
              className="font-display text-[11px] tracking-[0.22em] uppercase font-bold"
              style={{ color: "#8B2252" }}
            >
              Volume Discounts
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13.5px] leading-snug" style={{ color: "#1C1618" }}>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#8B2252" }} />
              Buy 2 vials → <strong className="font-display font-bold" style={{ color: "#8B2252" }}>5% off</strong>
            </span>
            <span className="hidden sm:inline text-charcoal-200">|</span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#8B2252" }} />
              Buy 3 vials → <strong className="font-display font-bold" style={{ color: "#8B2252" }}>8% off</strong>
            </span>
          </div>
          <span data-testid="volume-discount-note" className="sm:ml-auto text-[11px] tracking-wide italic" style={{ color: "#9a7a80" }}>
            {activeSeasonal
              ? `Automatically applied at checkout · not combined with ${activeSeasonal.name}`
              : "Automatically applied at checkout"}
          </span>
        </div>
      </section>

      {/* Header */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="overline mb-3">Research Compounds</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] text-charcoal-600 mb-4">
              The full catalog.
            </h1>
            <p className="text-charcoal-400 text-lg leading-relaxed">
              Every compound below is supplied lyophilized, sealed, and intended exclusively for laboratory and scientific research purposes.
            </p>
          </div>
        </div>
      </section>

      {/* Sticky category chip nav */}
      <div className="sticky top-16 md:top-20 z-30 bg-cream-100/85 backdrop-blur-lg border-y border-charcoal-100/40">
        <div className="container-x py-3">
          <div
            data-testid="category-chip-nav"
            className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-2 px-2"
          >
            {CATEGORIES.map((c) => {
              const isActive = c.id === activeCategory;
              return (
                <button
                  key={c.id}
                  data-testid={`chip-${c.id}`}
                  onClick={() => handleChipClick(c.id)}
                  className={`whitespace-nowrap shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-charcoal-600 text-cream-100 shadow-soft"
                      : "bg-white text-charcoal-500 hover:bg-sage-100 hover:text-charcoal-600 border border-charcoal-100/50"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category sections */}
      <div className="container-x mt-12 space-y-20 md:space-y-24">
        {CATEGORIES.map((c) => {
          const items = productsByCategory(products, c.id);
          if (items.length === 0) return null;
          return (
            <section
              key={c.id}
              id={c.id}
              data-category-id={c.id}
              data-testid={`category-section-${c.id}`}
              ref={(el) => { sectionRefs.current[c.id] = el; }}
              className="scroll-mt-32"
            >
              <div className="mb-8 md:mb-10 max-w-2xl">
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight text-charcoal-600 mb-2">
                  {c.label}
                </h2>
                <p className="text-sm text-charcoal-400">{items.length} compound{items.length === 1 ? "" : "s"}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {items.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <p className="container-x mt-16 text-xs text-charcoal-300 max-w-3xl leading-relaxed">
        For Research Use Only. Not for Human Consumption. Not intended to diagnose, treat, cure, or prevent any disease. Not FDA approved for diagnostic or therapeutic use.
      </p>
    </main>
  );
}
